import { useEffect, useState } from "react";
import { api, errMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Users, UserPlus, MoreHorizontal, Key, Ban, CheckCircle, Archive, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { FilterPanel } from "@/components/admin/FilterPanel";
import { BulkActionsBar } from "@/components/admin/BulkActionsBar";
import { useFilters } from "@/lib/useFilters";
import { useSelection } from "@/lib/useSelection";

const ROLE_OPTIONS = [
  { value: "admin", label: "System Administrator" },
  { value: "credentialing_reviewer", label: "Credentialing Reviewer" },
  { value: "accreditation_reviewer", label: "Accreditation Reviewer" },
  { value: "reviewer", label: "Reviewer (legacy combined)" },
  { value: "site_visitor", label: "Site Visitor" },
  { value: "read_only_auditor", label: "Read-Only Auditor" },
  { value: "applicant", label: "Applicant" },
  { value: "practitioner", label: "Practitioner" },
  { value: "school_applicant", label: "School Applicant" },
];

const USER_FILTERS = [
  { key: "search", type: "search", label: "Search by name or email", paths: ["name", "email"] },
  { key: "role", type: "select", label: "Role", path: "role", options: ROLE_OPTIONS },
  { key: "is_disabled", type: "boolean", label: "Disabled", path: "is_disabled" },
  { key: "is_archived", type: "boolean", label: "Archived", path: "is_archived" },
  { key: "is_deleted", type: "boolean", label: "Deleted", path: "is_deleted" },
  { key: "date_from", type: "dateFrom", label: "Created from", path: "created_at" },
  { key: "date_to", type: "dateTo", label: "Created to", path: "created_at" },
];

function roleLabel(v) {
  return ROLE_OPTIONS.find((r) => r.value === v)?.label || v;
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [purgeStep, setPurgeStep] = useState(0);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const filters = useFilters(users, USER_FILTERS, { defaultSort: { field: "created_at", dir: "desc" } });
  const selection = useSelection(filters.filtered);

  const changeRole = async (id, role) => {
    try { await api.patch(`/admin/users/${id}/role`, { role }); toast.success("Role updated"); reload(); }
    catch (e) { toast.error(errMessage(e)); }
  };
  const action = async (id, path, label) => {
    try { await api.post(`/admin/users/${id}/${path}`); toast.success(label); reload(); }
    catch (e) { toast.error(errMessage(e)); }
  };
  const softDelete = async (id) => {
    try { await api.delete(`/admin/users/${id}`); toast.success("User deleted"); reload(); }
    catch (e) { toast.error(errMessage(e)); }
  };
  const purgeUser = async (id) => {
    try {
      await api.post(`/admin/users/${id}/purge`, {}, { headers: { "X-Confirm-Purge": "yes" } });
      toast.success("User purged");
      setPurgeTarget(null);
      setPurgeStep(0);
      reload();
    } catch (e) { toast.error(errMessage(e)); }
  };

  const filtered = filters.filtered;

  return (
    <div className="animate-fade-up" data-testid="users-page">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Administration</div>
          <h1 className="font-heading text-3xl font-bold text-primary">User management</h1>
          <p className="text-sm text-slate-500 mt-1">Create administrators, reviewers, site visitors, and auditors. All actions are logged.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="open-create-user">
          <UserPlus className="h-4 w-4 mr-2" /> Create user
        </Button>
      </div>

      <FilterPanel
        schemas={USER_FILTERS}
        values={filters.values}
        set={filters.set}
        clear={filters.clear}
        count={filters.count}
        total={users.length}
        activeCount={filters.activeCount}
        sortFields={[
          { key: "created_at", label: "Created date" },
          { key: "name", label: "Name" },
          { key: "email", label: "Email" },
        ]}
        sort={filters.sort}
        setSort={filters.setSort}
        testid="users-filters"
      />

      <BulkActionsBar collection="users" selection={selection} onDone={reload} />

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
              <tr>
                <th className="p-4 w-10">
                  <Checkbox checked={selection.allSelected} onCheckedChange={selection.toggleAll} data-testid="users-select-all" />
                </th>
                <th className="p-4">User</th>
                <th className="p-4">Role</th>
                <th className="p-4">Status</th>
                <th className="p-4">Joined</th>
                <th className="p-4">Phone</th>
                <th className="p-4 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={7} className="p-8 text-center text-slate-500">Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="p-10 text-center">
                  <Users className="h-9 w-9 text-slate-300 mx-auto mb-2" />
                  <div className="text-sm text-slate-500">No users match</div>
                </td></tr>
              )}
              {filtered.map((u) => (
                <tr key={u.id} className={`hover:bg-slate-50 ${u.is_deleted || u.is_archived ? "opacity-50" : ""}`} data-testid={`user-row-${u.id}`}>
                  <td className="p-4">
                    <Checkbox checked={selection.isSelected(u.id)} onCheckedChange={() => selection.toggle(u.id)} data-testid={`user-select-${u.id}`} />
                  </td>
                  <td className="p-4">
                    <div className="font-medium text-primary">{u.name}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </td>
                  <td className="p-4">
                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                      <SelectTrigger className="h-8 w-[210px] text-xs" data-testid={`role-select-${u.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLE_OPTIONS.map((r) => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4">
                    {u.is_deleted ? <Badge variant="outline" className="bg-red-50 text-red-800 border-red-200">deleted</Badge>
                      : u.is_archived ? <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300">archived</Badge>
                      : u.is_disabled ? <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">disabled</Badge>
                      : <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">active</Badge>}
                  </td>
                  <td className="p-4 text-xs text-slate-600">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  <td className="p-4 text-xs text-slate-600">{u.phone || "—"}</td>
                  <td className="p-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`user-actions-${u.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => setEditTarget(u)} data-testid={`user-edit-${u.id}`}>
                          <Pencil className="h-4 w-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setResetTarget(u)} data-testid={`user-reset-${u.id}`}>
                          <Key className="h-4 w-4 mr-2" /> Reset password
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {u.is_disabled ? (
                          <DropdownMenuItem onClick={() => action(u.id, "enable", "Reactivated")} data-testid={`user-enable-${u.id}`}>
                            <CheckCircle className="h-4 w-4 mr-2" /> Reactivate
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => action(u.id, "disable", "Disabled")} data-testid={`user-disable-${u.id}`}>
                            <Ban className="h-4 w-4 mr-2" /> Disable
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => action(u.id, "archive", "Archived")} data-testid={`user-archive-${u.id}`}>
                          <Archive className="h-4 w-4 mr-2" /> Archive
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-red-700 focus:text-red-700" onClick={() => softDelete(u.id)} data-testid={`user-delete-${u.id}`}>
                          <Trash2 className="h-4 w-4 mr-2" /> Delete
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-red-700 focus:text-red-700" onClick={() => { setPurgeTarget(u); setPurgeStep(1); }} data-testid={`user-purge-${u.id}`}>
                          <Trash2 className="h-4 w-4 mr-2" /> Purge permanently…
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={reload} />
      <EditUserDialog target={editTarget} onClose={() => setEditTarget(null)} onSaved={reload} />
      <ResetPasswordDialog target={resetTarget} onClose={() => setResetTarget(null)} />
      <AlertDialog open={!!purgeTarget} onOpenChange={(o) => { if (!o) { setPurgeTarget(null); setPurgeStep(0); } else if (purgeStep === 0) setPurgeStep(1); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {purgeStep <= 1 ? `Purge ${purgeTarget?.email}?` : "Are you absolutely sure?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {purgeStep <= 1
                ? "This permanently removes the user from the database. Audit history is retained."
                : "This is your second and final confirmation. Press Purge to permanently delete the account."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-purge-cancel">Cancel</AlertDialogCancel>
            {purgeStep <= 1 ? (
              <AlertDialogAction
                className="bg-red-600 text-white hover:bg-red-700"
                onClick={(e) => { e.preventDefault(); setPurgeStep(2); }}
                data-testid="user-purge-step-1"
              >
                Continue
              </AlertDialogAction>
            ) : (
              <AlertDialogAction
                className="bg-red-600 text-white hover:bg-red-700"
                onClick={() => purgeUser(purgeTarget.id)}
                data-testid="user-purge-confirm"
              >
                Purge permanently
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateUserDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "applicant" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!open) setForm({ name: "", email: "", password: "", phone: "", role: "applicant" }); }, [open]);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      onCreated?.();
      onClose();
    } catch (e) { toast.error(errMessage(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle>Create user</DialogTitle>
          <DialogDescription>The new account is logged in the audit trail.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cu-name" /></div>
          <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cu-email" /></div>
          <div><Label className="text-xs">Initial password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="cu-password" /></div>
          <div><Label className="text-xs">Phone (optional)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cu-phone" /></div>
          <div>
            <Label className="text-xs">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="cu-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.email || !form.password || !form.name} data-testid="cu-submit">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ target, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  useEffect(() => { if (target) setForm({ name: target.name || "", email: target.email || "", phone: target.phone || "" }); }, [target]);
  const submit = async () => {
    try { await api.patch(`/admin/users/${target.id}`, form); toast.success("User updated"); onSaved?.(); onClose(); }
    catch (e) { toast.error(errMessage(e)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-user-dialog">
        <DialogHeader><DialogTitle>Edit user</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="eu-name" /></div>
          <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="eu-email" /></div>
          <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="eu-phone" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} data-testid="eu-submit">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ target, onClose }) {
  const [pw, setPw] = useState("");
  useEffect(() => { if (!target) setPw(""); }, [target]);
  const submit = async () => {
    try {
      await api.post(`/admin/users/${target.id}/password-reset`, { new_password: pw });
      toast.success(`Password reset for ${target.email}`);
      onClose();
    } catch (e) { toast.error(errMessage(e)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
          <DialogDescription>The new password takes effect immediately. Communicate it to the user securely.</DialogDescription>
        </DialogHeader>
        <Input type="password" placeholder="New password (min 8 characters)" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="rp-password" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={pw.length < 8} data-testid="rp-submit">Reset password</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
